import {Component, computed, OnDestroy, QueryList, signal, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {
    AssignProductsRequest,
    CreateProductGroupRequest,
    Customer,
    Product,
    ProductGroupSummary,
    UpdateProductGroupRequest
} from "@app/_models/api";
import {ActivatedRoute} from "@angular/router";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {AsyncPipe} from "@angular/common";
import {GlobalService} from "@app/_services/global.service";
import {BehaviorSubject, forkJoin, Observable, Subscription} from "rxjs";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {ProductGroupService} from "@app/_services/master-data/product-groups.service";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {TableSearchComponent} from "@app/_components/table-search/table-search.component";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

interface GroupForm {
    id: FormControl<string | null>;
    name: FormControl<string | null>;
}

@Component({
    selector: 'app-product-groups',
    templateUrl: './product-groups.component.html',
    styleUrl: '../../app.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        ReactiveFormsModule,
        AsyncPipe,
        ModalConfirmComponent,
        FormsModule,
        SortableDirective
    ]
})
export class ProductGroupsComponent implements OnDestroy {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('createModal') private createModal!: TemplateRef<unknown>;
    @ViewChild('editModal') private editModal!: TemplateRef<unknown>;
    @ViewChild('manageModal') private manageModal!: TemplateRef<unknown>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;

    createModalRef!: NgbModalRef;
    editModalRef!: NgbModalRef;
    manageModalRef!: NgbModalRef;
    private _customers$ = new BehaviorSubject<Customer[]>([]);
    private customersSubscription: Subscription;

    total$: Observable<number> | undefined;
    customers: Customer[] = [];
    customerId: string | undefined;
    savingData = false;

    createForm = new FormGroup<GroupForm>({
        id: new FormControl(""),
        name: new FormControl("", Validators.required)
    });

    editForm = new FormGroup<GroupForm>({
        id: new FormControl("", Validators.required),
        name: new FormControl("", Validators.required)
    });

    // --- Membership modal (dual-list transfer) local state — signals for zoneless change detection ---
    manageGroup = signal<ProductGroupSummary | null>(null);
    membershipLoading = signal(false);
    savingMembership = signal(false);
    private availableProducts = signal<Product[]>([]);
    private memberProducts = signal<Product[]>([]);
    availableSearch = signal("");
    memberSearch = signal("");
    selectedAvailable = signal<Set<string>>(new Set());
    selectedMember = signal<Set<string>>(new Set());
    private originalMemberIds = new Set<string>();

    filteredAvailable = computed(() => this.filterProducts(this.availableProducts(), this.availableSearch()));
    filteredMembers = computed(() => this.filterProducts(this.memberProducts(), this.memberSearch()));

    constructor(
        public globalService: GlobalService,
        private route: ActivatedRoute,
        private toastService: ToastService,
        private modalService: NgbModal,
        private apiService: ApiService,
        public service: ProductGroupService
    ) {
        this._customers$.next(this.route.snapshot.data['resolvedData'].data);
        this.customersSubscription = this._customers$.subscribe(customers => this.customers = customers);
        this.total$ = this.service.total$;
    }

    ngOnDestroy() {
        this.customersSubscription.unsubscribe();
    }

    onCustomerChange(event: Event) {
        this.customerId = (event.target as HTMLInputElement).value;
        this.loadGroups();
    }

    private loadGroups() {
        if (!this.customerId) return;

        this.globalService.showWaitOverlay();
        this.apiService.getProductGroups(this.customerId).subscribe({
            next: (groups: ProductGroupSummary[]) => {
                this.service.data = groups;
                this.service.refreshDataSource();
                this.globalService.hideWaitOverlay();
            },
            error: () => {
                this.toastService.showDanger("Request Failed", "Failed to load product groups. Please try again.");
                this.globalService.hideWaitOverlay();
            }
        });
    }

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) header.direction = '';
        }
        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    // --- Create ---
    onAdd() {
        this.createForm.reset();
        this.createModalRef = this.modalService.open(this.createModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static"
        });
    }

    onAddSubmit() {
        if (this.createForm.invalid || !this.customerId) {
            this.toastService.showDanger("Invalid Data", "Please provide a group name.");
            return;
        }

        this.savingData = true;
        const body: CreateProductGroupRequest = {name: this.createForm.controls.name.value!.trim()};
        this.apiService.createProductGroup(this.customerId, body).subscribe({
            next: (group) => {
                this.service.data = this.service.data.concat({
                    id: group.id,
                    name: group.name,
                    isAllProducts: group.isAllProducts,
                    memberCount: 0
                });
                this.service.refreshDataSource();
                this.toastService.showStandard("Success", "Product group created.");
                this.createModalRef.close();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", "Could not create the group. The name may already be in use.");
                throw e;
            },
            complete: () => this.savingData = false
        });
    }

    // --- Rename ---
    onEdit(element: ProductGroupSummary) {
        this.editForm.reset();
        this.editForm.patchValue({id: element.id, name: element.name});
        this.editModalRef = this.modalService.open(this.editModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static"
        });
    }

    onEditSubmit() {
        if (this.editForm.invalid || !this.customerId) {
            this.toastService.showDanger("Invalid Data", "Please provide a group name.");
            return;
        }

        this.savingData = true;
        const id = this.editForm.controls.id.value!;
        const body: UpdateProductGroupRequest = {name: this.editForm.controls.name.value!.trim()};
        this.apiService.updateProductGroup(this.customerId, id, body).subscribe({
            next: () => {
                this.service.data = this.service.data.map(g => g.id !== id ? g : {...g, name: body.name});
                this.service.refreshDataSource();
                this.toastService.showStandard("Success", "Product group renamed.");
                this.editModalRef.close();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", "Could not rename the group. The name may already be in use.");
                throw e;
            },
            complete: () => this.savingData = false
        });
    }

    // --- Delete ---
    onDelete(element: ProductGroupSummary) {
        this.modalConfirmComponent.open("Delete this product group and all of its product assignments?", element.name);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                if (!this.customerId) return;
                this.apiService.deleteProductGroup(this.customerId, element.id).subscribe({
                    next: () => {
                        this.service.data = this.service.data.filter(g => g.id !== element.id);
                        this.service.refreshDataSource();
                        this.toastService.showStandard("Success", "Product group deleted.");
                    },
                    error: (e) => {
                        this.toastService.showDanger("Request Failed", "Could not delete the group.");
                        throw e;
                    }
                });
            },
            () => {
            }
        );
    }

    // --- Membership (dual-list transfer) ---
    onManageProducts(element: ProductGroupSummary) {
        if (!this.customerId) return;

        this.manageGroup.set(element);
        this.availableProducts.set([]);
        this.memberProducts.set([]);
        this.selectedAvailable.set(new Set());
        this.selectedMember.set(new Set());
        this.availableSearch.set("");
        this.memberSearch.set("");
        this.membershipLoading.set(true);

        this.manageModalRef = this.modalService.open(this.manageModal, {
            modalDialogClass: "modal-dialog-centered modal-xl",
            backdrop: "static"
        });

        forkJoin({
            all: this.apiService.getProducts(this.customerId),
            detail: this.apiService.getProductGroup(this.customerId, element.id)
        }).subscribe({
            next: ({all, detail}) => {
                const memberIds = new Set(detail.products.map(p => p.id));
                this.originalMemberIds = new Set(memberIds);
                this.memberProducts.set(this.sortByName(detail.products));
                this.availableProducts.set(this.sortByName(all.filter(p => !memberIds.has(p.id))));
                this.membershipLoading.set(false);
            },
            error: () => {
                this.toastService.showDanger("Request Failed", "Failed to load products for this group.");
                this.membershipLoading.set(false);
                this.manageModalRef.dismiss();
            }
        });
    }

    toggleAvailable(id: string) {
        this.selectedAvailable.set(this.toggle(this.selectedAvailable(), id));
    }

    toggleMember(id: string) {
        this.selectedMember.set(this.toggle(this.selectedMember(), id));
    }

    moveToMembers() {
        const selected = this.selectedAvailable();
        if (selected.size === 0) return;
        const moving = this.availableProducts().filter(p => selected.has(p.id));
        this.memberProducts.set(this.sortByName([...this.memberProducts(), ...moving]));
        this.availableProducts.set(this.availableProducts().filter(p => !selected.has(p.id)));
        this.selectedAvailable.set(new Set());
    }

    moveToAvailable() {
        const selected = this.selectedMember();
        if (selected.size === 0) return;
        const moving = this.memberProducts().filter(p => selected.has(p.id));
        this.availableProducts.set(this.sortByName([...this.availableProducts(), ...moving]));
        this.memberProducts.set(this.memberProducts().filter(p => !selected.has(p.id)));
        this.selectedMember.set(new Set());
    }

    // Move every currently-visible (search-filtered) available product into the group.
    moveAllToMembers() {
        const visible = this.filteredAvailable();
        if (visible.length === 0) return;
        const visibleIds = new Set(visible.map(p => p.id));
        this.memberProducts.set(this.sortByName([...this.memberProducts(), ...visible]));
        this.availableProducts.set(this.availableProducts().filter(p => !visibleIds.has(p.id)));
        this.selectedAvailable.set(this.removeIds(this.selectedAvailable(), visibleIds));
    }

    // Move every currently-visible (search-filtered) group member back to available.
    moveAllToAvailable() {
        const visible = this.filteredMembers();
        if (visible.length === 0) return;
        const visibleIds = new Set(visible.map(p => p.id));
        this.availableProducts.set(this.sortByName([...this.availableProducts(), ...visible]));
        this.memberProducts.set(this.memberProducts().filter(p => !visibleIds.has(p.id)));
        this.selectedMember.set(this.removeIds(this.selectedMember(), visibleIds));
    }

    get membershipDirty(): boolean {
        const current = this.memberProducts();
        if (current.length !== this.originalMemberIds.size) return true;
        return current.some(p => !this.originalMemberIds.has(p.id));
    }

    onSaveMembership() {
        const group = this.manageGroup();
        if (!group || !this.customerId) return;

        const currentIds = new Set(this.memberProducts().map(p => p.id));
        const toAdd = [...currentIds].filter(id => !this.originalMemberIds.has(id));
        const toRemove = [...this.originalMemberIds].filter(id => !currentIds.has(id));

        if (toAdd.length === 0 && toRemove.length === 0) {
            this.manageModalRef.close();
            return;
        }

        const calls: Observable<unknown>[] = [];
        if (toAdd.length > 0) {
            const body: AssignProductsRequest = {productIds: toAdd};
            calls.push(this.apiService.assignProductsToGroup(this.customerId, group.id, body));
        }
        if (toRemove.length > 0) {
            const body: AssignProductsRequest = {productIds: toRemove};
            calls.push(this.apiService.unassignProductsFromGroup(this.customerId, group.id, body));
        }

        this.savingMembership.set(true);
        forkJoin(calls).subscribe({
            next: () => {
                const newCount = this.memberProducts().length;
                this.service.data = this.service.data.map(g => g.id !== group.id ? g : {...g, memberCount: newCount});
                this.service.refreshDataSource();
                this.toastService.showStandard("Success", "Product assignments updated.");
                this.savingMembership.set(false);
                this.manageModalRef.close();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", "Could not update product assignments.");
                this.savingMembership.set(false);
                throw e;
            }
        });
    }

    private toggle(set: Set<string>, id: string): Set<string> {
        const next = new Set(set);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    }

    private removeIds(set: Set<string>, toRemove: Set<string>): Set<string> {
        const next = new Set(set);
        for (const id of toRemove) next.delete(id);
        return next;
    }

    private filterProducts(products: Product[], term: string): Product[] {
        const t = term.trim().toLowerCase();
        if (!t) return products;
        return products.filter(p =>
            p.name.toLowerCase().includes(t) || (p.genericName?.toLowerCase().includes(t) ?? false));
    }

    private sortByName(products: Product[]): Product[] {
        return [...products].sort((a, b) => a.name.localeCompare(b.name));
    }
}
